import React, {useEffect, useState, useMemo, useRef} from 'react';
import {
  Box,
  VStack,
  Center,
  Spinner,
  useTheme,
  Flex,
  IconButton,
  Heading,
  useToken,
  HStack,
} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import SwipeCard, {SwipeCardRef} from '../../components/SwipeCard';
import {Event, Offer, Place, Post} from '../../types/article';
import {usePlaceTypes} from '../../hooks/usePlaceTypes';
import useGetUserLocation from '../../hooks/useUserLocation';
import {useEvents} from '../../hooks/useEvents';
import {useOffers} from '../../hooks/useOffers';
import {useInfinitePlacesByType} from '../../hooks/useFetchPlacesByType';
import {motion} from 'framer-motion';
import EventCard from '../../components/Articles/EventCard';
import PlaceCard from '../../components/Articles/PlaceCard';
import OfferCard from '../../components/Articles/Offers/OfferCard';
import {TextA18, TextG14} from '../../components/typography';
import {BlueButton} from '../../components/button';
import Icon from '../../components/Icon';
import {useSelector} from 'react-redux';
import BackButton from '../../components/BackButton';
import styled from 'styled-components';
import SwipeEffect from '../../components/SwipeCard/SwipeEffect';
import {useInfinitePlaces} from '../../hooks/usePlaces';

// Interface for active effects
interface ActiveEffect {
  id: string;
  direction: 'left' | 'right';
  isVisible: boolean;
}

type SwipeCollection = 'events' | 'places' | 'offers';

const CategoryPill = styled(motion.button)<{isActive: boolean; color: string}>`
  padding: 10px 18px;
  border-radius: 30px;
  background-color: ${(props) => (props.isActive ? props.color : 'transparent')};
  color: ${(props) => (props.isActive ? 'white' : props.color)};
  border: 2px solid ${(props) => props.color};
  font-weight: ${(props) => (props.isActive ? 'bold' : 'normal')};
  font-size: 14px;
  box-shadow: ${(props) => (props.isActive ? '0 4px 10px rgba(0, 0, 0, 0.15)' : 'none')};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  user-select: none;

  &:active {
    transform: scale(0.95);
  }
`;

const ActionButton = styled(motion.button)<{bgColor: string}>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${(props) => props.bgColor};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: manipulation;

  &:active {
    transform: scale(0.95);
  }

  &::selection {
    background: transparent;
  }
`;

const CardStackContainer = styled(Box)`
  min-height: 400px;
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;
  transform-style: preserve-3d;
  overflow: visible;
  contain: layout paint;
`;

const SwipeInterface = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userLocation = useGetUserLocation();
  const {placeTypesLoading, getIdsForCategory} = usePlaceTypes();
  const selectedCommunityId = useSelector((state: any) => state.community.community)?.id;

  const topCardRef = useRef<SwipeCardRef>(null);

  const [blue, green, red] = useToken('colors', ['blue', 'green', 'red']);

  const [currentCollection, setCurrentCollection] = useState<SwipeCollection>('events');
  const [swipedItems, setSwipedItems] = useState<Record<string, 'left' | 'right'>>({});
  const [isAnimating, setIsAnimating] = useState(false);

  const [activeEffects, setActiveEffects] = useState<ActiveEffect[]>([]);

  const {events, eventsIsLoading} = useEvents({communityId: selectedCommunityId?.toString()});
  const {offers, offersIsLoading} = useOffers({communityId: selectedCommunityId?.toString()});

  const foodPlaceIds: string[] = useMemo(() => {
    if (!placeTypesLoading) {
      return [
        ...getIdsForCategory('Places to eat'),
        ...getIdsForCategory('Bars'),
        ...getIdsForCategory('Coffee & Desserts'),
      ];
    } else return [];
  }, [placeTypesLoading]);

  const fetchFoodPlaces = useInfinitePlacesByType({
    lat: userLocation?.data?.latitude,
    long: userLocation?.data?.longitude,
    open: 'open',
    typeIds: foodPlaceIds,
    sort: 'ai',
    communityId: selectedCommunityId?.toString(),

    enabled: !placeTypesLoading,
  });
    
  const foodPlaces = fetchFoodPlaces.data?.pages[0]?.data?.data || [];

  const activeItems = useMemo(() => {
    switch (currentCollection) {
      case 'events':
        return events || [];
      case 'places':
        return foodPlaces || [];
      case 'offers':
        return offers || [];
      default:
        return [];
    }
  }, [currentCollection, events, foodPlaces, offers]);

  const activeUnswipedItems = useMemo(() => {
    return activeItems.filter((item) => !swipedItems[item.id.toString()]);
  }, [activeItems, swipedItems]);

  const visibleStack = useMemo(() => {
    return activeUnswipedItems.slice(0, 5);
  }, [activeUnswipedItems]);

  const handleSwipeLeft = (item: Event | Place | Offer | Post) => {
    if (isAnimating) return;
    setIsAnimating(true);

    const effectId = `effect-${Date.now()}`;
    const newEffect: ActiveEffect = {
      id: effectId,
      direction: 'left',
      isVisible: true,
    };

    setActiveEffects((prev) => [...prev, newEffect]);

    // Updated timeout to match the SwipeEffect animation duration (0.65s) plus max delay (0.1s)
    setTimeout(() => {
      setActiveEffects((prev) => prev.filter((effect) => effect.id !== effectId));
    }, 750); // 650ms (animation) + 100ms (max delay)

    setTimeout(() => {
      setSwipedItems((prev) => ({
        ...prev,
        [item.id.toString()]: 'left',
      }));
      setIsAnimating(false);
    }, 200);
  };

  const handleSwipeRight = (item: Event | Place | Offer | Post) => {
    if (isAnimating) return;
    setIsAnimating(true);

    const effectId = `effect-${Date.now()}`;
    const newEffect: ActiveEffect = {
      id: effectId,
      direction: 'right',
      isVisible: true,
    };

    setActiveEffects((prev) => [...prev, newEffect]);

    // Updated timeout to match the SwipeEffect animation duration (0.65s) plus max delay (0.1s)
    setTimeout(() => {
      setActiveEffects((prev) => prev.filter((effect) => effect.id !== effectId));
    }, 750); // 650ms (animation) + 100ms (max delay)

    setTimeout(() => {
      setSwipedItems((prev) => ({
        ...prev,
        [item.id.toString()]: 'right',
      }));
      setIsAnimating(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      setActiveEffects([]);
    };
  }, []);

  const isLoading = useMemo(() => {
    switch (currentCollection) {
      case 'events':
        return eventsIsLoading;
      case 'places':
        return fetchFoodPlaces.isLoading || placeTypesLoading;
      case 'offers':
        return offersIsLoading;
      default:
        return false;
    }
  }, [
    currentCollection,
    eventsIsLoading,
    fetchFoodPlaces.isLoading,
    placeTypesLoading,
    offersIsLoading,
  ]);

  useEffect(() => {
    setSwipedItems({});
  }, [currentCollection]);

  const renderCard = (item: Event | Place | Offer | Post) => {
    switch (currentCollection) {
      case 'events':
        return <EventCard event={item as Event} hideRSVP isSlide />;
      case 'places':
        return <PlaceCard place={item as Place} isSlide />;
      case 'offers':
        return <OfferCard offer={item as Offer} />;
      default:
        return null;
    }
  };

  const navigateToDetail = (item: Event | Place | Offer | Post) => {
    if (!item) return;

    switch (currentCollection) {
      case 'events':
        navigate(`/event/${item.id}`);
        break;
      case 'places':
        navigate(`/place/${item.id}`);
        break;
      case 'offers':
        break;
    }
  };

  const getCategoryIcon = (category: SwipeCollection) => {
    switch (category) {
      case 'events':
        return 'fi fi-rr-calendar';
      case 'places':
        return 'fi fi-rr-marker';
      case 'offers':
        return 'fi fi-rr-ticket';
    }
  };

  return (
    <Box width="100%" pb={4} overflow="hidden">
      {activeEffects.map((effect) => (
        <SwipeEffect
          key={effect.id}
          id={effect.id}
          isVisible={effect.isVisible}
          direction={effect.direction}
        />
      ))}

      <Flex
        w="100%"
        justifyContent="space-between"
        alignItems="center"
        py={4}
        px={4}
        bg="white"
        boxShadow="0 2px 10px rgba(0,0,0,0.05)">
        <BackButton sticky={false} onClick={() => navigate('/home')} />
        <Heading size="md" textAlign="center" flex={1}>
          Discover
        </Heading>
        <Box w="40px" />
      </Flex>

      <HStack
        justifyContent="center"
        spacing={3}
        px={4}
        py={5}
        overflowX="auto"
        css={{
          '&::-webkit-scrollbar': {display: 'none'},
          scrollbarWidth: 'none',
        }}>
        {(['events', 'places', 'offers'] as SwipeCollection[]).map((category) => (
          <CategoryPill
            key={category}
            isActive={currentCollection === category}
            color={category === 'events' ? blue : category === 'places' ? green : red}
            onClick={() => setCurrentCollection(category)}
            whileTap={{scale: 0.95}}
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}>
            <Flex alignItems="center" gap={2}>
              <Icon iconName={getCategoryIcon(category)} />
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </Flex>
          </CategoryPill>
        ))}
      </HStack>

      <Center
        flex="1"
        minH="400px"
        maxH={{base: '60vh', md: '500px', lg: '600px'}}
        position="relative"
        overflow="visible"
        pointerEvents="auto"
        css={{
          contain: 'layout paint style',
          perspective: '1200px',
        }}>
        {isLoading ? (
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor={theme.colors.lightGrey}
            color={theme.colors.lightBlue}
            size="lg"
          />
        ) : activeUnswipedItems.length === 0 ? (
          <motion.div
            initial={{opacity: 0, scale: 0.9}}
            animate={{opacity: 1, scale: 1}}
            transition={{duration: 0.3}}>
            <Center>
              <Flex
                direction="column"
                gap={4}
                p={6}
                bg="white"
                borderRadius="lg"
                boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                maxWidth="90%"
                textAlign="center">
                <TextA18 fontWeight="bold">No more {currentCollection} right now!</TextA18>
                <TextG14>
                  We'll add more {currentCollection} soon. You can try another category.
                </TextG14>
                <BlueButton
                  aria-label="Refresh"
                  variant="outline"
                  onClick={() => setSwipedItems({})}>
                  <Flex alignItems="center" gap={2}>
                    <Icon iconName="fi fi-rr-refresh" style={{fontSize: '16px'}} />
                    Reset Swiped Items
                  </Flex>
                </BlueButton>
              </Flex>
            </Center>
          </motion.div>
        ) : (
          <CardStackContainer>
            {visibleStack.map((item, index) => (
              <SwipeCard
                key={`${item.id}-${index}`}
                ref={index === 0 ? topCardRef : null}
                item={item}
                onSwipeLeft={handleSwipeLeft}
                onSwipeRight={handleSwipeRight}
                renderCard={renderCard}
                itemType={
                  currentCollection === 'events'
                    ? 'event'
                    : currentCollection === 'places'
                    ? 'place'
                    : 'offer'
                }
                index={index}
                totalCards={visibleStack.length}
              />
            ))}
          </CardStackContainer>
        )}
      </Center>

      {activeUnswipedItems.length > 0 && (
        <HStack justifyContent="center" spacing={4} mt={6}>
          <ActionButton
            bgColor={red}
            onClick={() => {
              if (activeUnswipedItems.length > 0 && topCardRef.current) {
                topCardRef.current.swipeLeft();
              }
            }}
            whileTap={{scale: 0.9}}
            whileHover={{scale: 1.1}}
            transition={{type: 'spring', stiffness: 400, damping: 17}}>
            <Icon iconName="fi fi-rr-time-past" style={{fontSize: '24px'}} />
          </ActionButton>

          <ActionButton
            bgColor={'white'}
            whileHover={{scale: 1.1}}
            whileTap={{scale: 0.8}}
            onClick={() => navigateToDetail(visibleStack[0])}>
            <Icon iconName="fi fi-rr-info" style={{fontSize: '24px', color: 'blue'}} />
          </ActionButton>

          <ActionButton
            bgColor={blue}
            onClick={() => {
              if (activeUnswipedItems.length > 0 && topCardRef.current) {
                topCardRef.current.swipeRight();
              }
            }}
            whileTap={{scale: 0.9}}
            whileHover={{scale: 1.1}}
            transition={{type: 'spring', stiffness: 400, damping: 17}}>
            <Icon iconName="fi fi-rr-bookmark" style={{fontSize: '24px'}} />
          </ActionButton>
        </HStack>
      )}
    </Box>
  );
};

export default SwipeInterface;
